import { useContext, useEffect, useState } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import greenCircleTick from '../../../images/green-circle-tick.png';
import redCircleCross from '../../../images/red-circle-cross.png';
import '../../BusinessPartners/BusinessPartnerCard.css';
import { StoreContext } from '../../Store';
import { getQAPersistedData, postOrderForm } from '../../apiCalls';
import ComplianceChecklistTable from './ComplianceChecklistTable';
import DataCheckTable from './DataCheckTable';
import FinancialsTable from './FinancialsTable';
import FloatingBar from './FloatingBar/FloatingBar';
import ItemChecklist from './ItemChecklist';
import './QAChecklistTab.css';
import { QAStoreContext } from './QAStore';
import QAErrors from './QAErrors/QAErrors';
import TariffModal from './modals/TariffModal';
import {
  fetchRevenuePayableData,
  getConfigRoute,
  getHeaderValues,
  getIsVatLiable,
  getQAConfig,
  getValueFromID,
  convertToGbp,
  getFormDataToOrderForms,
  getDutyRatesFromItems,
  isChedDoc,
  filterPrefsByInvoiceOrigin,
  fetchAdjExchangeRate,
  customsValue,
  calculateVatLiability,
  calculateDutyLiability,
  dutyHasQuotasApplicable,
  getPortType,
  getPortName,
  fetchCheckModalData,
  validatePreferenceData,
  validateSpreadsheetData,
  showCheckOrView,
  getControlledGoodsError,
} from './utils';
import OverviewModal from './modals/OverviewModal';
import { useSecurity } from '../../Security/security';
import { VatTypeEnum, getVatEnumValue } from '../../../utils/enums';
import PreferenceCheckModal from './modals/PreferenceCheckModal/PreferenceCheckModal';
import { getQAPreferenceDetails } from '../../../api/newLoad';

const QAChecklistTab = ({
  visible,
  submitLoadAsDraft,
  submitLoad,
  headerErrors,
  readOnly,
  selectedForms,
  loadId = null,
  spreadsheetHandle,
  productItemsStatus,
}) => {
  // --------------------
  // State Declarations
  // --------------------
  const { validateRole } = useSecurity();
  const [showOverviewModal, setShowOverviewModal] = useState(false);
  const [disableSaveButtons, setDisableSaveButtons] = useState(false);
  const [showPreferenceCheckModal, setShowPreferenceCheckModal] = useState(false);

  const [persistedData, setPersistedData] = useState([]);
  const [checkModalData, setCheckModalData] = useState({
    euCountries: [],
    thirdCountries: [],
  });
  const [isPreferenceCheckValid, setIsPreferenceCheckValid] = useState(false);
  const [entryPort, setEntryPort] = useState('');
  const [exitPort, setExitPort] = useState('');
  const [incotermsData, setIncotermsData] = useState({
    validIncoterm: false,
    incoTermText: '',
    doesIncotermRequireFreightCharge: false,
    incotermLocationSet: false,
  });
  const [isComplianceChecklistValid, setIsComplianceChecklistValid] = useState(false);
  const [isDataCheckValid, setIsDataCheckValid] = useState(false);
  const [isFinancialsValid, setIsFinancialsValid] = useState(false);
  const [tariffModalData, setTariffModalData] = useState(null);
  const [typeErrorMessage, setTypeErrorMessage] = useState(null);
  const [dutyRatesError, setDutyRatesError] = useState(false);
  const [hasExciseGoods, setHasExciseGoods] = useState(false);

  // --------------------
  // Context & Variable Declarations
  // --------------------
  const store = useContext(StoreContext);
  const qaStore = useContext(QAStoreContext);
  const { fetchPortModelFromPortID, fetchConsigneeData, fetchConsignorData, resetQAStore } = qaStore;
  const setNewLoadQaVat = qaStore.newLoadQaVat[1];
  const [currentConfig, setCurrentConfig] = qaStore.currentConfig;
  const [initialDataFetched, setInitialDataFetched] = qaStore.initialDataFetched;
  const vatRate = currentConfig.vat.rate;
  const exchangeRate = store.exchangeRate[0];

  const portID = store.GBimport[0] ? store.entry[0] : store.exit[0];
  const portOptions = store.GBimport[0] ? store.entryPortOptions[0] : store.exitPortOptions[0];

  const portModelID = getPortType(portOptions, portID) ? store.portsModal[0] : qaStore.nonHybridPortModelID[0];
  const financialsClass = currentConfig?.visibleFields?.financialsTable ? 'flex-fill' : 'd-none';

  // whether QA screen is loading it's initial data
  const [isLoading, setIsLoading] = useState(true);

  // --------------------
  // Effects
  // --------------------

  const updateRevenuePayableData = async ({ documents, products, itemTotals }) => {
    if (store.consignee[0] && documents) {
      await fetchConsigneeData(store.consignee[0]);

      const isAllVatZ = products?.every((item) => item.vat === VatTypeEnum.VATZ);
      const oneOrMoreVatS = products?.some((item) => item.vat === VatTypeEnum.VATS);
      const { hasFFD, hasGBImport, hasEUImportEntry } = documents;
      const options = {
        customerAllowSAOnNeeNors: store.QACustomerData[0]?.allowSAOnNeeNors,
        isConsigneeBP: !!store.QAConsigneeData[0]?.cabieStatus,
        vatTotal: itemTotals?.vatLiabilityGbp,
        isGBImport: hasGBImport || hasFFD,
        hasEUImportEntry,
        orderFromRepresentative: store.representative[0],
        isAllVatZ,
        oneOrMoreVatS,
        isPVA: store.QAConsigneeData[0]?.isPVA,
      };
      await fetchRevenuePayableData(store.consignee[0], setNewLoadQaVat, options);
    }
  };

  const fetchDutyRates = async (documents) => {
    const { hasFFD, hasGBImport } = documents ?? qaStore.currentConfig[0].documents;

    const vatAdjustment = Number(store.adjustments[0]?.find((adj) => adj.name === 'vatAdjustment')?.amount || 0);
    const vatCurrency = await fetchAdjExchangeRate(
      store.currencyOptions[0],
      Number(store.adjustments[0]?.find((adj) => adj.name === 'vatAdjustment')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    const vatAdjustmentGbp = Number(convertToGbp(vatAdjustment, vatCurrency));

    const products = await updateCustomsValue(store.validatedRowsReadyToSubmit[0] ?? []);

    const dutyRate = await getDutyRatesFromItems(
      products,
      exchangeRate,
      vatAdjustmentGbp,
      store.RoiImport[0],
      hasFFD || hasGBImport
    );

    return dutyRate;
  };

  const fetchOrderFormData = async () => {
    const roleIsValid = validateRole({ department: 'QA' });
    const postData = getFormDataToOrderForms(store, roleIsValid);
    const results = await postOrderForm(postData);
    const filteredDocs = results?.[0]?.Docs.map((doc) => {
      const isChed = isChedDoc(doc);
      return {
        ...doc,
        checked: selectedForms.includes(doc.formname),
        qty: doc.qty ?? (isChed ? 1 : doc.qty),
      };
    });

    return filteredDocs;
  };

  const fetchPersistedData = async (products) => {
    let prefData = store.preferenceData[0];

    // fetch preference check modal validation data based on products
    const productPreferences = products.map((p) => ({
      origin: p.origin,
      preferential: p.preference?.toLowerCase() === 'yes',
    }));
    const modalData = await fetchCheckModalData(productPreferences);
    setCheckModalData(modalData);

    // requests which should only be fetched once after a draft load is opened
    if (!initialDataFetched && loadId) {
      const data = await getQAPersistedData(loadId);
      if (data[0]?.mopConfirmed !== undefined || data[0]?.mopSelection !== undefined) {
        store.customsMoPConfirmation[1]({
          confirmed: !!data[0]?.mopConfirmed,
          selection: data[0]?.mopSelection ?? null,
        });
      }

      // some persisted data is only displayed for submitted loads
      if (readOnly) {
        setPersistedData(data);
      }

      // fetching existing preference data for the load
      prefData = await getQAPreferenceDetails(loadId);
      setInitialDataFetched(true);
    }

    // Check that preference data only contains data for products on the spreadsheet - remove any data that isn't needed
    const filteredPreferenceData = filterPrefsByInvoiceOrigin(prefData, products, store.invoicenumber[0]);
    store.preferenceData[1](filteredPreferenceData);

    return filteredPreferenceData;
  };

  const refreshQAConfig = (documents) => {
    const configRoute = getConfigRoute(
      !!store.GBimport[0],
      store.directionSelected[0],
      selectedForms,
      documents ?? store.docTypes[0],
      store.representative[0],
      store.QACustomerData[0].allowSAOnNeeNors
    );

    const newConfig = getQAConfig(configRoute);
    setCurrentConfig({ ...newConfig });

    return newConfig;
  };

  const updateIncotermData = (documents) => {
    const routesRequiringIncotermLocation = ['EU (mainland) - GB', 'ROI - GB', 'ROI - GB via NI', 'ROW - GB'];
    const requiresIncotermLocation = routesRequiringIncotermLocation.includes(
      getValueFromID(store.movementOptions[0], store.directionSelected[0])
    );

    const hasEuImportEntry = documents?.hasEUImportEntry;
    const incotermsWithRequiredFreightCharge = ['EXW', 'FCA', 'FAS', 'FOB'];
    const incotermValueFromID = getValueFromID(store.incotermsOptions[0], store.selectincoterm[0]);

    const incotermLocationSet = parseInt(store.selectincoterm[0]) === 7 ? !!store.incotermLocation[0] : true;
    // include incotermLocationSet only when the route requires an incoterm location
    let validIncoterm =
      !!store.selectincoterm[0] &&
      !!store.incotermsOptions[0].length &&
      (requiresIncotermLocation ? incotermLocationSet : true);

    const doesIncotermRequireFreightCharge = incotermsWithRequiredFreightCharge.includes(incotermValueFromID);

    //logic for CABS-2631
    if (incotermValueFromID === 'DDP' && hasEuImportEntry) {
      validIncoterm = true;
    }

    setIncotermsData({
      validIncoterm: validIncoterm,
      incoTermText: incotermValueFromID,
      doesIncotermRequireFreightCharge: doesIncotermRequireFreightCharge,
      incotermLocationSet: incotermLocationSet,
      incotermLocation: store.incotermLocation[0] ? ` - ${store.incotermLocation[0]}` : '',
    });
  };

  const getProductListAndTotals = async (preProducts, spreadsheetErrors, dutyRateData, documents) => {
    const { hasFFD, hasGBImport } = documents || {};

    const totals = {
      packageCount: 0,
      grossMass: 0,
      netMass: 0,
      value: 0,
      valueGbp: 0,
      dutyLiabilityGbp: 0,
      vatLiabilityGbp: 0,
      itemRows: 0,
      customsValue: 0,
    };

    let hasExcise = false;
    const products = await updateCustomsValue(preProducts);
    const updatedProductList = products.map((product, idx) => {
      const lineNumber = idx + 1;
      const PackageCount = +product.packageCount;
      const GrossMass = +product.grossMass;
      const NetMass = +product.netMass;
      const itemValue = +product.itemValue;
      const customsValue = +product.customsValue;

      const errorDetails = spreadsheetErrors?.[idx] || {};
      const hasError = errorDetails?.hasError || false;

      const isVatLiable = getIsVatLiable(product.VAT);

      const vatLiability = calculateVatLiability(dutyRateData, idx, isVatLiable, product.vatLiability, readOnly);
      const dutyLiability = calculateDutyLiability(dutyRateData, idx, product.dutyLiability, readOnly);
      const productDutyRates = dutyRateData[idx];
      const checkOrView = showCheckOrView(productDutyRates);
      hasExcise = hasExcise || productDutyRates?.excise || false;

      totals.packageCount += PackageCount || 0;
      totals.grossMass += GrossMass || 0;
      totals.netMass += NetMass || 0;
      totals.value += itemValue || 0;

      if (vatLiability === 'CALCULATION') {
        totals.vatLiabilityGbp = 'CALCULATION';
      } else if (totals.vatLiabilityGbp !== 'CALCULATION') {
        totals.vatLiabilityGbp += isVatLiable ? parseFloat(vatLiability) || 0 : 0;
      }

      if (dutyLiability === 'CALCULATION') {
        totals.dutyLiabilityGbp = 'CALCULATION';
      } else if (totals.dutyLiabilityGbp !== 'CALCULATION') {
        totals.dutyLiabilityGbp += parseFloat(dutyLiability) || 0;
      }

      totals.customsValue += customsValue || itemValue;
      const productPreference = product.preferential
        ? product.preferential.replace('1', 'YES').replace('2', 'NO')
        : 'NO';
      const itemDutyRates = dutyRateData?.[idx] || {};
      return {
        item: lineNumber,
        invoiceNumber: product.invoiceNumber,
        consignorId: product.neeNoridconsignor,
        valid: <img src={hasError ? redCircleCross : greenCircleTick} width={26} height={26} alt="Valid" />,
        hasError: hasError,
        errorDetails: errorDetails,
        hsCode: product.commoditycode,
        origin: product.origin,
        packages: PackageCount,
        grossKg: GrossMass.toFixed(2),
        netKg: NetMass.toFixed(2),
        value: itemValue.toFixed(2),
        preference: productPreference,
        dutyLiability: parseFloat(dutyLiability) !== 0 ? dutyLiability : '-',
        vatLiability: vatLiability,
        vat: getVatEnumValue(product?.VAT),
        customsValue: product.customsValue ? product.customsValue.toFixed(2) : itemValue.toFixed(2),
        tariff: (
          <button
            className={`btn btn-link ${checkOrView === 'check' && 'qa_items_check_label'}`}
            onClick={() => {
              setTariffModalData({
                commodityCode: product.commoditycode,
                origin: product.origin,
                lineNumber: lineNumber,
                preference: product.preferential || 'NO',
                isVatLiable: isVatLiable,
                thirdCountryAV: itemDutyRates.thirdCountryAV,
                thirdCountrySpecific: itemDutyRates.thirdCountrySpecific,
                dutyExpressionRate: itemDutyRates.dutyExpressionRate,
                Quotas: dutyHasQuotasApplicable(itemDutyRates),
                vatRate: itemDutyRates.vatRate,
                additionalDuties: itemDutyRates.additionalDuties,
                excise: itemDutyRates.excise,
                customsTech: itemDutyRates.customsTech,
              });
            }}
          >
            {checkOrView === 'check' ? 'Check' : 'View'}
          </button>
        ),
      };
    });
    totals.uniqueInvoiceHeaders = getHeaderValues(products);
    totals.valueGbp = hasFFD || hasGBImport ? totals.customsValue : convertToGbp(totals.value, exchangeRate);
    totals.itemRows = products.length;

    return { productList: updatedProductList, totals, hasExcise };
  };

  // Check if ports have loaded; GB-ROW and ROW-GB routes don't require both of them so skip the wait for those directions
  const entryPortsLoaded = !!store.entryPortOptions[0].length || Number(store.directionSelected[0]) === 12; // GB - ROW
  const exitPortsLoaded = !!store.exitPortOptions[0].length || Number(store.directionSelected[0]) === 13; // ROW - GB
  const hasLoadingDate = !!store.date[0];
  const hasInvoiceCurrency = !!store.invoicecurr[0];
  const canShowItemChecklist = hasInvoiceCurrency && hasLoadingDate;

  const headerOptionsLoaded =
    !!store.loadTypeOptions[0].length &&
    entryPortsLoaded &&
    exitPortsLoaded &&
    (!!exchangeRate || !hasInvoiceCurrency || !hasLoadingDate); // exchange rate loaded, or currency/date not selected yet

  useEffect(() => {
    const run = async () => {
      setIsLoading(true);

      const preProducts = store.validatedRowsReadyToSubmit[0] ?? [];

      // 1. FETCH ORDER FORM DATA
      const orderFormData = store.docTypes[0].length ? store.docTypes[0] : await fetchOrderFormData();
      store.docTypes[1](orderFormData);

      // 2. UPDATE QA CONFIG BASED ON RELEVANT DATA
      const newConfig = refreshQAConfig(orderFormData);

      // 3. FETCH CONSIGNOR DATA
      if (store.consignor[0]) {
        await fetchConsignorData(store.consignor[0]);
      }

      // 4. FETCH DUTY RATES
      const dutyRate = await fetchDutyRates(newConfig.documents);
      const dutyRateData = dutyRate.data || [];
      store.dutyRates[1](dutyRateData);
      setDutyRatesError(!!dutyRate.error);

      // 5. SET CURRENCY IN QA STORE
      if (store.invoicecurr[0]) {
        const currency = store.currencyOptions[0]?.find((c) => c.id === store.invoicecurr[0]?.toString())?.data;
        qaStore.invoiceCurrency[1](currency);
      }

      // 6. GET CONTROLLED GOODS ERROR
      const controlledGoodsError = getControlledGoodsError(
        dutyRateData,
        orderFormData,
        store.controlledGoods[0],
        spreadsheetHandle
      );
      setTypeErrorMessage(controlledGoodsError);

      // 7. SET ENTRY/EXIT PORT NAMES
      setEntryPort(getPortName(store.entryPortOptions[0], store.entry[0]));
      setExitPort(getPortName(store.exitPortOptions[0], store.exit[0]));

      // 8. SET PORT MODEL
      if (portID) {
        fetchPortModelFromPortID(portID);
      }

      // 9. FIND SPREADSHEET ERRORS
      let spreadsheetErrors = [];
      if (!productItemsStatus && !readOnly) {
        // only run validation if we haven't done so already
        spreadsheetErrors = await validateSpreadsheetData(spreadsheetHandle, store);
      } else {
        spreadsheetErrors = productItemsStatus;
      }

      // 10. SET PRODUCT LIST AND TOTALS
      const { productList, totals, hasExcise } = await getProductListAndTotals(
        preProducts,
        spreadsheetErrors,
        dutyRateData,
        newConfig.documents
      );
      setHasExciseGoods(hasExcise);
      qaStore.itemTotals[1](totals);
      store.QAProductsList[1](productList);

      // 11. FETCH PERSISTED DATA FOR LOAD - INCLUDING PREFERENCE DATA
      const prefData = await fetchPersistedData(productList);

      // 12. VALIDATE PREFERENCE DATA
      const preferenceDataIsValid = validatePreferenceData(prefData, productList, store.invoicenumber[0], exchangeRate);
      setIsPreferenceCheckValid(preferenceDataIsValid);

      // 13. UPDATE INCOTERM DATA
      updateIncotermData(newConfig?.documents);

      setIsLoading(false);
    };

    // Only run this effect when the load is ready, the tab is visible, and we have the necessary data to perform the calculations
    // loadTypeOptions - if this has loaded, then so have other options (such as currencies, incoterms .etc) - we just check one rather than all to avoid unnecessarily long dependency arrays
    const loadIsReady = store.GBimport[0] !== null || store.GBexport[0] !== null;
    if (loadIsReady && visible && headerOptionsLoaded) {
      run();
    }
  }, [store.validatedRowsReadyToSubmit[0], productItemsStatus, vatRate, visible, headerOptionsLoaded]);

  // Updates for revenue payable data (Customs MoP, VAT .etc)
  // TODO: Move this logic to main useEffect - this cannot be done until logic from ComplianceChecklist and FinancialsTable are moved also
  useEffect(() => {
    if (
      visible &&
      store.QACustomerData[0].id &&
      Object.keys(qaStore.currentConfig[0].documents).length > 0 &&
      store.QAProductsList[0].length > 0 &&
      qaStore.itemTotals[0].itemRows > 0
    ) {
      updateRevenuePayableData({
        documents: qaStore.currentConfig[0].documents,
        products: store.QAProductsList[0],
        itemTotals: qaStore.itemTotals[0],
      });
    }
  }, [
    visible,
    store.QACustomerData[0],
    store.QAConsigneeData[0],
    qaStore.currentConfig[0].documents,
    store.QAProductsList[0],
    qaStore.itemTotals[0],
  ]);

  // Refresh config if the customer data is ever changed
  useEffect(() => {
    if (visible && !isLoading) {
      refreshQAConfig();
    }
  }, [visible, store.QACustomerData[0], store.QAConsigneeData[0]]);

  useEffect(() => {
    return () => {
      resetQAStore();
    };
  }, []);

  const updateCustomsValue = async (load) => {
    if (readOnly) return load;

    const freightCharges = Number(store.adjustments[0]?.find((adj) => adj.name === 'freightCharges')?.amount || 0);
    const insuranceCharges = Number(store.adjustments[0]?.find((adj) => adj.name === 'insuranceCharges')?.amount || 0);
    const otherCharges = Number(store.adjustments[0]?.find((adj) => adj.name === 'otherCharges')?.amount || 0);
    const invoiceDiscount = Number(store.adjustments[0]?.find((adj) => adj.name === 'invoiceDiscount')?.amount || 0);
    const freightCurrency = await fetchAdjExchangeRate(
      store.currencyOptions[0],
      Number(store.adjustments[0]?.find((adj) => adj.name === 'freightCharges')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    const insuranceCurrency = await fetchAdjExchangeRate(
      store.currencyOptions[0],
      Number(store.adjustments[0]?.find((adj) => adj.name === 'insuranceCharges')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    const otherCurrency = await fetchAdjExchangeRate(
      store.currencyOptions[0],
      Number(store.adjustments[0]?.find((adj) => adj.name === 'otherCharges')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );
    const invoiceCurrency = await fetchAdjExchangeRate(
      store.currencyOptions[0],
      Number(store.adjustments[0]?.find((adj) => adj.name === 'invoiceDiscount')?.currency || 0),
      store.date[0],
      store.exchangeRateCache
    );

    if (store.ffd[0] || store.GBimport[0]) {
      const updatedItems = load.map((row, i) => {
        row.customsValue = customsValue(
          row.itemValue,
          store.exchangeRate[0],
          freightCharges,
          insuranceCharges,
          otherCharges,
          invoiceDiscount,
          freightCurrency,
          insuranceCurrency,
          otherCurrency,
          invoiceCurrency,
          store.totalItemValue[0]
        );
        return row;
      });
      load = updatedItems;
    }
    const updatedLoad = load;
    return updatedLoad;
  };

  if (isLoading) {
    return (
      <Container fluid style={{ borderRadius: '20px', backgroundColor: '#D4E3FC', padding: 50, textAlign: 'center' }}>
        <Spinner animation="border" size="lg" />
      </Container>
    );
  }

  return (
    <div style={{ display: 'initial' }}>
      <QAErrors exchangeRate={exchangeRate} dutyRatesError={dutyRatesError} />

      <OverviewModal
        show={showOverviewModal}
        onHide={() => setShowOverviewModal(false)}
        headerErrors={headerErrors}
        productErrors={store.QAProductsList[0]?.filter((p) => p.hasError)}
      />
      <PreferenceCheckModal
        isOpen={showPreferenceCheckModal}
        onClose={() => setShowPreferenceCheckModal(false)}
        setIsPreferenceCheckValid={setIsPreferenceCheckValid}
        countries={checkModalData}
        products={store.QAProductsList[0]}
        readOnly={readOnly}
        exchangeRate={exchangeRate}
        isReadOnly={readOnly}
      />

      <TariffModal {...tariffModalData} handleClose={setTariffModalData} />
      <Container fluid style={{ borderRadius: '20px', backgroundColor: '#D4E3FC', padding: 0 }}>
        <div className="d-flex gap-4 p-4">
          <div className="flex-fill" style={{ maxWidth: '33%' }}>
            <h3 className="qa-section-header">Compliance Checklist</h3>
            <ComplianceChecklistTable
              setIsComplianceChecklistValid={setIsComplianceChecklistValid}
              readOnly={readOnly}
              loadId={loadId}
              persistedData={persistedData}
              config={currentConfig}
              incoterm={incotermsData.incoTermText}
              displayCheckModal={checkModalData.euCountries.length > 0 || checkModalData.thirdCountries.length > 0}
              isPreferenceCheckValid={isPreferenceCheckValid}
              setShowPreferenceCheckModal={setShowPreferenceCheckModal}
              typeErrorMessage={typeErrorMessage}
            />
          </div>

          <div className={financialsClass} style={{ maxWidth: '33%' }}>
            <h3 className="qa-section-header">Financials</h3>
            <FinancialsTable
              setIsFinancialsValid={setIsFinancialsValid}
              incotermsData={incotermsData}
              readOnly={readOnly}
            />
          </div>

          <div className="flex-fill" style={{ maxWidth: '33%' }}>
            <h3 className="qa-section-header">Data Check</h3>
            <DataCheckTable
              portID={portID}
              portModelID={portModelID}
              entryPort={entryPort}
              exitPort={exitPort}
              setIsDataCheckValid={setIsDataCheckValid}
              readOnly={readOnly}
            />
          </div>
        </div>
        <div className="p-4" style={{ backgroundColor: '#FFFFFF' }}>
          {canShowItemChecklist ? (
            <ItemChecklist productList={store.QAProductsList[0]} />
          ) : (
            <div className="text-center" style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              {!hasInvoiceCurrency && <p>Please select a currency to view the items in the checklist.</p>}
              {!hasLoadingDate && <p>Please select a date of loading to view the items in the checklist.</p>}
            </div>
          )}
        </div>

        <FloatingBar
          isVisible={visible}
          submitLoadAsDraft={submitLoadAsDraft}
          submitLoad={submitLoad}
          isComplianceChecklistValid={isComplianceChecklistValid}
          isDataCheckValid={isDataCheckValid}
          isFinancialsValid={isFinancialsValid}
          headerErrors={headerErrors}
          productItemsHasErrors={store.QAProductsList[0]?.some((product) => product.hasError)}
          onOverviewClick={() => setShowOverviewModal(true)}
          readOnly={readOnly}
          disableSaveButtons={disableSaveButtons}
          portModelID={portModelID}
          hasExciseGoods={hasExciseGoods}
          hasQuota={store.dutyRates[0]?.some((rate) => dutyHasQuotasApplicable(rate))}
        />
      </Container>
    </div>
  );
};

export default QAChecklistTab;
